import { useCallback, useEffect, useRef, useState } from 'react';
import {
	cancelForkSweep,
	compareForks,
	forkAnalysis,
	getForkSweepState,
	listForks,
	previewForkSweep,
	startForkSweep,
} from 'api/forkApi';
import type { ForkCompareParams, ForkSweepBody, ForkSweepFilters } from 'api/forkApi';
import { getStoredBacktestsByIds } from 'api/backtestApi';
import { ApiError } from 'api/restClient';
import { ruleKey, type BacktestRuleSet } from 'hooks/useBacktest';
import type {
	BacktestRollup,
	ForkCompareResponse,
	ForkListResponse,
	ForkPreview,
	ForkRequest,
	ForkResponse,
	ForkRunState,
} from 'types/history';

const POLL_MS = 2000;

function errMessage(e: unknown): string {
	if (e instanceof ApiError) {
		if (e.status === 403) return 'Forking analyses is admin-only.';
		if (e.status === 409) return 'A fork sweep is already running.';
		return `${e.message} (${e.status})`;
	}
	return e instanceof Error ? e.message : 'Fork failed';
}

/**
 * Fork one analysis onto another engine.
 *
 * Synchronous on the wire — the request is held for the whole run, which on
 * `brainer_v1` is an Opus 5 call of several minutes. `running` is therefore a
 * long-lived state that the caller must show, not a flicker.
 */
export function useForkOne(): {
	running: boolean;
	result: ForkResponse | null;
	error: string | null;
	fork: (analysisId: string, body: ForkRequest) => Promise<ForkResponse | null>;
	reset: () => void;
} {
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<ForkResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const fork = useCallback(async (analysisId: string, body: ForkRequest) => {
		setRunning(true);
		setError(null);
		setResult(null);
		try {
			const res = await forkAnalysis(analysisId, body);
			if (mounted.current) setResult(res);
			return res;
		} catch (e) {
			if (mounted.current) setError(errMessage(e));
			return null;
		} finally {
			if (mounted.current) setRunning(false);
		}
	}, []);

	const reset = useCallback(() => {
		setResult(null);
		setError(null);
	}, []);

	return { running, result, error, fork, reset };
}

/**
 * The batch fork sweep: preview → start → poll → cancel.
 *
 * Preview is a separate step on purpose. A sweep is unattended spend — a
 * few hundred v1 forks is three figures — so the cost has to be visible before
 * the click that commits to it, not after.
 */
export function useForkSweep(): {
	state: ForkRunState | null;
	preview: ForkPreview | null;
	previewing: boolean;
	error: string | null;
	loadPreview: (body: ForkSweepBody, filters: ForkSweepFilters) => void;
	clearPreview: () => void;
	start: (body: ForkSweepBody, filters: ForkSweepFilters) => void;
	cancel: () => void;
} {
	const [state, setState] = useState<ForkRunState | null>(null);
	const [preview, setPreview] = useState<ForkPreview | null>(null);
	const [previewing, setPreviewing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, []);

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	}, []);

	const beginPolling = useCallback(() => {
		stopPolling();
		pollRef.current = setInterval(() => {
			getForkSweepState()
				.then((s) => {
					if (!mounted.current) return;
					setState(s);
					if (s.status !== 'running') stopPolling();
				})
				.catch(() => stopPolling());
		}, POLL_MS);
	}, [stopPolling]);

	// On mount, surface a sweep already running — started in another tab, or
	// before a reload. These run for a long time, so "no sweep" would otherwise be
	// the default answer for most of a sweep's life.
	useEffect(() => {
		getForkSweepState()
			.then((s) => {
				if (!mounted.current) return;
				setState(s);
				if (s.status === 'running') beginPolling();
			})
			.catch(() => undefined);
	}, [beginPolling]);

	const loadPreview = useCallback((body: ForkSweepBody, filters: ForkSweepFilters) => {
		setPreviewing(true);
		setError(null);
		previewForkSweep(body, filters)
			.then((p) => {
				if (mounted.current) setPreview(p);
			})
			.catch((e: unknown) => {
				if (mounted.current) setError(errMessage(e));
			})
			.finally(() => {
				if (mounted.current) setPreviewing(false);
			});
	}, []);

	const clearPreview = useCallback(() => {
		setPreview(null);
		setError(null);
	}, []);

	const start = useCallback(
		(body: ForkSweepBody, filters: ForkSweepFilters) => {
			setError(null);
			startForkSweep(body, filters)
				.then((s) => {
					if (!mounted.current) return;
					setState(s);
					setPreview(null);
					beginPolling();
				})
				.catch((e: unknown) => {
					if (mounted.current) setError(errMessage(e));
				});
		},
		[beginPolling],
	);

	const cancel = useCallback(() => {
		cancelForkSweep()
			.then((s) => {
				if (mounted.current) setState(s);
			})
			.catch((e: unknown) => {
				if (mounted.current) setError(errMessage(e));
			});
	}, []);

	return { state, preview, previewing, error, loadPreview, clearPreview, start, cancel };
}

/**
 * One parent and its forks, with each column's outcome attached.
 *
 * Two calls, because each already exists and does its half correctly:
 *   1. `listForks` — the runs and what they proposed.
 *   2. `getStoredBacktestsByIds` — rollups for every column at once, keyed by the
 *      rule set the caller is viewing.
 *
 * The decisive proposal is then picked client-side with `decisiveProposal`, the
 * same helper the Backtest tab uses. That is deliberate: doing it server-side too
 * would create a second implementation of "decisive" that could drift, and the two
 * surfaces would quietly disagree about which trade a run is judged on.
 */
export function useForkList(
	analysisId: string | null,
	rules: BacktestRuleSet,
): {
	data: ForkListResponse | null;
	/** audit id → its stored rollup, absent when that run has not been swept. */
	rollups: Map<string, BacktestRollup>;
	loading: boolean;
	error: string | null;
	reload: () => void;
} {
	const [data, setData] = useState<ForkListResponse | null>(null);
	const [rollups, setRollups] = useState<Map<string, BacktestRollup>>(new Map());
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [nonce, setNonce] = useState(0);

	// Rules are a fresh object each render; key on their values.
	const rulesKey = ruleKey(rules);

	useEffect(() => {
		if (!analysisId) {
			setData(null);
			setRollups(new Map());
			return;
		}
		const ctrl = new AbortController();
		setLoading(true);
		setError(null);

		listForks(analysisId, ctrl.signal)
			.then(async (res) => {
				if (ctrl.signal.aborted) return;
				setData(res);
				const ids = [res.parent.id, ...res.forks.map((f) => f.id)];
				// Verdicts are a separate concern from the runs themselves: a sweep
				// may not have happened yet, and that must render as "not scored"
				// rather than failing the whole panel.
				try {
					const stored = await getStoredBacktestsByIds(
						rules.window, rules.closeWindow, rules.stopMult, rules.replayVersion,
						ids, ctrl.signal,
					);
					if (ctrl.signal.aborted) return;
					setRollups(new Map(stored.map((r) => [r.audit_id, r])));
				} catch {
					if (!ctrl.signal.aborted) setRollups(new Map());
				}
			})
			.catch((e: unknown) => {
				if (!ctrl.signal.aborted) setError(errMessage(e));
			})
			.finally(() => {
				if (!ctrl.signal.aborted) setLoading(false);
			});

		return () => ctrl.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [analysisId, rulesKey, nonce]);

	const reload = useCallback(() => setNonce((n) => n + 1), []);

	return { data, rollups, loading, error, reload };
}

/** Head-to-head engine comparison over one parent set. */
export function useForkCompare(params: ForkCompareParams | null): {
	data: ForkCompareResponse | null;
	loading: boolean;
	error: string | null;
	reload: () => void;
} {
	const [data, setData] = useState<ForkCompareResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [nonce, setNonce] = useState(0);

	// Serialised so a caller can rebuild the params object each render without
	// re-firing the request — the engines array in particular is rarely memoised.
	const key = params ? JSON.stringify(params) : null;

	useEffect(() => {
		if (!key) {
			setData(null);
			return;
		}
		const ctrl = new AbortController();
		setLoading(true);
		setError(null);
		compareForks(JSON.parse(key) as ForkCompareParams, ctrl.signal)
			.then(setData)
			.catch((e: unknown) => {
				if (!ctrl.signal.aborted) setError(errMessage(e));
			})
			.finally(() => {
				if (!ctrl.signal.aborted) setLoading(false);
			});
		return () => ctrl.abort();
	}, [key, nonce]);

	const reload = useCallback(() => setNonce((n) => n + 1), []);

	return { data, loading, error, reload };
}
